/**
 * SignatureRoutingSettings.tsx — V4-3-11 / ADR-0029.
 *
 * Settings → Team: per-lane routing editor with defaults, material
 * rule, GC role assignment. Audit event on change.
 *
 * Route: /w/:slug/app/settings/signatures (workspace-scoped, admin+).
 */

import { useState } from 'react';
import {
  Button,
  Card,
  PageHeader,
  Spinner,
} from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import { req, ApiError } from '../lib/api';

interface RoutingConfig {
  required_roles: string[];
  material_requires_gc: boolean;
}

async function getRouting(slug: string, lane: string): Promise<RoutingConfig> {
  const fallback: RoutingConfig = { required_roles: ['system_owner'], material_requires_gc: lane === 'building' };
  try {
    const body = await req<{ routing?: RoutingConfig }>(
      `/api/w/${encodeURIComponent(slug)}/suppliers/risk-bar?lane=${lane}`
    );
    return body?.routing ?? fallback;
  } catch (e) {
    if (e instanceof ApiError) return fallback;
    throw e;
  }
}

const ALL_ROLES = ['system_owner', 'tprm_lead', 'gc', 'grc_lead', 'product_owner'];
const ROLE_LABELS: Record<string, string> = {
  system_owner: 'System Owner',
  tprm_lead: 'TPRM Lead',
  gc: 'General Counsel',
  grc_lead: 'GRC Lead',
  product_owner: 'Product Owner',
};

export function SignatureRoutingSettings() {
  const { workspace } = useWorkspace();
  const buildingRouting = useWorkspaceQuery(
    (slug) => queryKeys.signatureRouting(slug, 'building'),
    (slug) => getRouting(slug, 'building'),
  );
  const buyingRouting = useWorkspaceQuery(
    (slug) => queryKeys.signatureRouting(slug, 'buying'),
    (slug) => getRouting(slug, 'buying'),
  );

  if (buildingRouting.loading || buyingRouting.loading) return <Spinner label="Loading routing" />;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Signature Routing"
        subtitle="Configure who signs system entries before a release."
      />

      <RoutingPanel
        lane="building"
        label="Building lane"
        config={buildingRouting.data ?? { required_roles: ['system_owner'], material_requires_gc: true }}
        slug={workspace.slug}
      />

      <RoutingPanel
        lane="buying"
        label="Buying lane"
        config={buyingRouting.data ?? { required_roles: ['system_owner'], material_requires_gc: false }}
        slug={workspace.slug}
      />
    </div>
  );
}

function RoutingPanel({
  lane,
  label,
  config,
  slug,
}: {
  lane: string;
  label: string;
  config: RoutingConfig;
  slug: string;
}) {
  const [roles, setRoles] = useState<string[]>(config.required_roles);
  const [gcRequired, setGcRequired] = useState(config.material_requires_gc);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const laneColor = lane === 'buying' ? 'var(--buying)' : 'var(--building)';

  return (
    <Card>
      <div style={{
        padding: 'var(--space-4)',
        borderLeft: `4px solid ${laneColor}`,
        display: 'grid',
        gap: 'var(--space-3)',
      }}>
        <h3 style={{
          margin: 0,
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-md)',
          fontWeight: 600,
          color: 'var(--text)',
        }}>
          {label}
        </h3>

        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-2xs)',
            color: 'var(--text-muted)',
            marginBottom: 'var(--space-2)',
          }}>
            Required signers
          </legend>
          {ALL_ROLES.map((role) => (
            <label
              key={role}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-2xs)',
                color: 'var(--text)',
                padding: '4px 0',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={roles.includes(role)}
                onChange={(e) => {
                  setSaved(false);
                  setRoles((prev) =>
                    e.target.checked ? [...prev, role] : prev.filter((r) => r !== role)
                  );
                }}
              />
              {ROLE_LABELS[role] ?? role}
            </label>
          ))}
        </fieldset>

        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text)',
          cursor: 'pointer',
        }}>
          <input
            type="checkbox"
            checked={gcRequired}
            onChange={(e) => { setSaved(false); setGcRequired(e.target.checked); }}
          />
          Require General Counsel on material entries
        </label>

        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <Button
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              setSaved(false);
              try {
                await req(`/api/w/${encodeURIComponent(slug)}/suppliers/risk-bar`, {
                  method: 'POST',
                  body: JSON.stringify({
                    bar_json: { routing: { required_roles: roles, material_requires_gc: gcRequired } },
                  }),
                });
                setSaved(true);
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {saved && (
            <span style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-3xs)', color: 'var(--success-fg)' }}>
              Saved
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
